import {
    parseISO,
    format,
    isAfter,
    isEqual,
    startOfDay,
    isValid,
} from 'date-fns';
import { getDateLocale } from '../../lib/dateLocale';

type Props = {
    ini: string;
    end: string;
    dateWithTime?: boolean;
};

/**
 * Safely parses a date string.
 * Returns null for empty, invalid, or "0000-00-00" dates.
 */
function safeParse(date: string | null | undefined): Date | null {
    if (!date || date.startsWith('0000-00-00')) return null;
    const parsed = parseISO(date);
    return isValid(parsed) ? parsed : null;
}

/**
 * Label for period. Green if the current date fall within the period, red otherwise.
 */
export function SttLabelPeriod({
    ini,
    end,
    dateWithTime = false,
}: Props): React.ReactElement | null {
    const locale = getDateLocale();

    const mIni = safeParse(ini);
    const mEnd = safeParse(end);

    // Return null if both dates are invalid
    if (!mIni && !mEnd) {
        return null;
    }

    const now = new Date();
    const today = startOfDay(now);
    const iniDay = mIni ? startOfDay(mIni) : today;
    const endDay = mEnd ? startOfDay(mEnd) : today;

    // Check if current date is within period (comparing at day granularity)
    const inTime =
        (isAfter(today, iniDay) || isEqual(today, iniDay)) &&
        (isAfter(endDay, today) || isEqual(endDay, today));

    const currentYear = format(now, 'yyyy');

    const formatDate = (date: Date, withTime: boolean) => {
        const year = format(date, 'yyyy');
        if (year === currentYear) {
            return format(date, 'd MMM', { locale });
        }
        return format(date, withTime ? 'd MMM yyyy HH:mm' : 'd MMM yyyy', {
            locale,
        });
    };

    const iniText = mIni ? formatDate(mIni, dateWithTime) : '';
    const endText = mEnd ? formatDate(mEnd, dateWithTime) : '';

    return (
        // Tintas de confirmación y error del sistema, no las palabras clave de
        // CSS: `red` es #ff0000 y se queda en 4:1 sobre blanco. Estas dan
        // 7,65:1 y 5,44:1.
        <span
            style={{
                color: inTime ? 'var(--ok-ink)' : 'var(--destructive)',
            }}
        >
            {iniText}
            {ini !== end && mEnd && ` - ${endText}`}
        </span>
    );
}
